import {
  IPdfDefinitions,
  IPdfData,
  PdfContentType,
  IPdfValueParams,
  PdfFiledValue,
  PdfContentSetting,
  CHARGEABLE_TIPS_DEFINE,
  CHARGEABLE_COLUMN_DEFINE,
  CHARGEABLE_SELECT_COLUMN_DEFINE,
} from '@app/shared/common/make-pdf/v2';
import {
  ITransferFormFieldPath,
  IInventoryItem,
  TRANSFER_PDF_EMPTY_HEADERS,
  TRANSFER_PDF_EMPTY_HEADERS_FOR_OPEN_STATUS,
  TRANSFER_TYPE,
} from '@app/shared/types';
import {
  generateSpacesHeader,
  generateSpacesText,
  groupByMajorMinorCategoryAndSort,
  getSingleExportFileName,
  formatDateWithTimeZone,
  isCategoryView,
} from '@app/shared/utils';
import { ContentText, Style } from 'pdfmake/interfaces';
import { get, map, isNil, isEqual, find } from 'lodash';
import { DecimalNumber } from '@app/shared/common';
import { TIME_FORMATS } from '@config/global-constants';
import { checkBoxMeasureBuilder } from '@app/shared/common/make-pdf/v2/cell-builders/check-box-measure.builder';
import {
  transferPDFQuantityFormatter,
  transferTypeFormatter,
  calculateResultOfDivFactorValue,
  filterQohFromColumn,
  getDefaultOrSelectedUnitEntityId,
  transferStatusFormatter,
} from './transfer.util';
import { PdfWorkerSelfType } from '@app/shared/common/make-pdf/pdf-worker.type';
import { findCurrentFactorValue } from '../orders/order.util';
import { TRANSFER_FORM_FIELD_PATHS } from './transfer-form-field-paths';
import { IPdfTableHeader } from '@app/shared/common/make-pdf/pdf-table-header.type';
import { dataSourceFormatter } from '@app/shared/formatters';
import { IPdfSpecialOptions } from './transfer-export.type';
import { I18n } from '@app/i18n/strings-keys';

declare const self: PdfWorkerSelfType;

type TransferWrittenSheetPdfConfigBuilder = (
  data: IPdfDefinitions['data'],
  isShowAllColumns: boolean,
  bulkOptions?: IPdfDefinitions['bulkOptions'],
  specialOptions?: IPdfSpecialOptions,
) => IPdfDefinitions;

const getTransferWrittenSheetConfigBuilder = (
  formFieldPath: ITransferFormFieldPath,
): TransferWrittenSheetPdfConfigBuilder => {
  return (
    data: IPdfDefinitions['data'],
    isShowAllColumns: boolean,
    bulkOptions?: IPdfDefinitions['bulkOptions'],
    specialOptions?: IPdfSpecialOptions,
  ): IPdfDefinitions => {
    const { showQoh = true, isTransferGroup = false } = specialOptions || {};

    return {
      bulkOptions,
      data,
      generateFileName: (pdfData): string => {
        return getSingleExportFileName(
          'Transfer Summary',
          pdfData?.name,
          pdfData.transaction_date,
        );
      },
      generateHeader: (pdfData: IPdfData, tools): ContentText[] => {
        return [
          {
            text: generateSpacesHeader(
              tools.services.i18nService.instant(
                I18n.Forms.TransferForm.TransferSummary,
              ),
              pdfData.internal_number,
            ),
            fontSize: 14,
            bold: true,
            margin: [20, 20, 20, 0],
          },
        ];
      },
      content: {
        summary: {
          type: PdfContentType.Summary,
          setting: (_, __, tools) => ({
            fields: [
              {
                [I18n.Forms.TransferForm.TransferType]: {
                  path: 'transfer_type',
                  width: '30%',
                  valueGetter: ({ value }: IPdfValueParams): string => {
                    return transferTypeFormatter(
                      { value },
                      { i18nService: tools.services.i18nService },
                    );
                  },
                },
                [I18n.Common.From]: {
                  path: 'site_name',
                  valueGetter: (params): string =>
                    generateSpacesText(
                      params.value,
                      `${params.data?.store_number}`,
                    ),
                  width: '30%',
                },
                [I18n.Common.Status]: {
                  path: 'status',
                  width: '30%',
                  valueFormatter: (params) =>
                    transferStatusFormatter(params, {
                      i18nService: tools.services.i18nService,
                    }),
                },
              },
              {
                [I18n.Forms.TransferForm.TransferDate]: {
                  path: 'transaction_date',
                  valueGetter: (params): string =>
                    formatDateWithTimeZone(
                      params.value?.toString(),
                      params.tools.services.companyService.currentCompany
                        ?.timezone,
                      TIME_FORMATS.L,
                    ),
                  width: '30%',
                },
                [I18n.Common.To]: {
                  valueGetter: (params: IPdfValueParams): string =>
                    generateSpacesText(
                      params.data?.site_name_to?.toString(),
                      params?.data.store_number_to?.toString(),
                    ),
                  width: '30%',
                },
                [I18n.EntityNames.Event]: {
                  path: 'event_name',
                  width: '30%',
                  hidden: (params): boolean => {
                    return isEqual(
                      params.data.transfer_type,
                      TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
                    );
                  },
                },
              },
              {
                [I18n.Common.Source]: {
                  path: 'source',
                  width: '30%',
                  valueFormatter: (params) =>
                    dataSourceFormatter(params as any, {
                      i18nService: tools.services.i18nService,
                    }),
                },
                [I18n.Forms.TransferForm.TransferGroupName]: {
                  path: TRANSFER_FORM_FIELD_PATHS.transferGroupName,
                  width: '70%',
                  hidden: (): boolean => {
                    return !isTransferGroup;
                  },
                },
              },
            ],
          }),
        },
        tips: CHARGEABLE_TIPS_DEFINE,
        mainGrids: {
          type: PdfContentType.Grids,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          setting: (pdfData: IPdfData, options): PdfContentSetting => {
            const isOpenTransfer = pdfData.isOpenTransfer;
            const displayQoh = pdfData?.displayQoh;
            const hiddenQohFromColumn = (): boolean => !displayQoh;

            return {
              [formFieldPath.virtualInventoryItemVenues]: {
                title: ({ gridData, tools }): string => {
                  return isCategoryView(
                    tools.services?.stateService?.getTransferItemListViewSetting(),
                  )
                    ? tools.services.i18nService.instant(
                        I18n.Forms.GridForm.ExportMajorMinorTitle,
                        {
                          majorCategory:
                            gridData[0]?.major_category_name ??
                            gridData[0]?.majorCategory ??
                            '',
                          minorCategory:
                            gridData[0]?.minor_category_name ??
                            gridData[0]?.minorCategory ??
                            '',
                        },
                      )
                    : null;
                },
                columns: [
                  CHARGEABLE_COLUMN_DEFINE,
                  {
                    path: 'item_name',
                    title: I18n.Forms.GridForm.ItemName,
                    width: '15%',
                  },
                  {
                    path: 'gl_account.name',
                    title: I18n.Forms.GridForm.GLAccount,
                    width: '10%',
                  },
                  {
                    hiddenColumn: hiddenQohFromColumn,
                    path: 'qoh_from',
                    title: I18n.Forms.TransferForm.QOHFrom,
                    width: '10%',
                    valueFormatter: (params): string => {
                      const value = findCurrentFactorValue(
                        params.rowData,
                        params.rowData.selected_measure_entity_id,
                      );
                      const complexData = {
                        value: params.value,
                        tools: params.tools,
                        pendingQty: params?.rowData?.pending_qty_from,
                        measureFactorValue: value,
                      };

                      return calculateResultOfDivFactorValue(
                        complexData,
                        false,
                      );
                    },
                  },
                  {
                    path: 'qoh_to',
                    title: I18n.Forms.TransferForm.QOHTo,
                    width: '10%',
                    valueFormatter: (params): string => {
                      const value = findCurrentFactorValue(
                        params.rowData,
                        params.rowData.selected_measure_entity_id,
                      );
                      const complexData = {
                        value: params.value,
                        tools: params.tools,
                        pendingQty: params?.rowData?.pending_qty_to,
                        measureFactorValue: value,
                      };

                      return calculateResultOfDivFactorValue(
                        complexData,
                        false,
                      );
                    },
                  },
                  {
                    path: 'measures',
                    title: I18n.Forms.TransferForm.TransferUnit,
                    width: '*',
                    builder: checkBoxMeasureBuilder,
                  },
                  {
                    path: isOpenTransfer ? 'item_cost' : 'initial_item_cost',
                    title: I18n.Forms.GridForm.UnitCost,
                    rowSpan: 1,
                    width: isOpenTransfer ? '*' : null,
                    alignment: 'right',
                    valueFormatter: ({ tools, ...params }): string =>
                      tools.services.numberFormatService.getNumberFormatter(
                        true,
                      )(params),
                  },
                  {
                    path: 'initial_count',
                    title: I18n.Forms.TransferForm.QtyRequested,
                    alignment: 'right',
                    rowSpan: isOpenTransfer ? null : 1,
                    width: 'auto',
                    valueFormatter: ({ tools, ...params }): string => {
                      if (isOpenTransfer) return '';

                      return transferPDFQuantityFormatter({ tools, ...params });
                    },
                  },
                  {
                    hiddenColumn: (): boolean => isOpenTransfer,
                    path: 'total_cost',
                    title: I18n.Forms.TransferForm.RequestCost,
                    rowSpan: 1,
                    alignment: 'right',
                    valueFormatter: ({ tools, ...params }): string => {
                      return isNil(params.rowData.initial_count)
                        ? ''
                        : tools.services.numberFormatService.getNumberFormatter(
                            true,
                          )(params);
                    },
                    valueGetter: (params): DecimalNumber | string => {
                      const unitCost =
                        params.rowData[
                          isOpenTransfer ? 'item_cost' : 'initial_item_cost'
                        ];
                      const totalCost = !isNil(params.rowData.initial_count)
                        ? new DecimalNumber(params.rowData.initial_count).mul(
                            unitCost,
                          )
                        : '';

                      return totalCost;
                    },
                  },
                  {
                    hiddenColumn: (): boolean => isOpenTransfer,
                    path: 'quantity_received_empty',
                    title: I18n.Forms.TransferForm.QtyReceived,
                    alignment: 'right',
                    valueFormatter: transferPDFQuantityFormatter,
                  },
                ],
                multiRow: {
                  baseOnRowFieldPath: 'measures',
                  splitter: (rowData, gridData, grid): IPdfData[] => {
                    // if have the measureEntityId use it else use first value
                    const selectedMeasureId = getDefaultOrSelectedUnitEntityId(
                      rowData.measure_entity_id,
                      rowData.measures,
                    );

                    return map(
                      get(rowData, grid.multiRow.baseOnRowFieldPath, []) as any,
                      (splitData) => {
                        return {
                          measure_description: splitData.description,
                          measure_factor_value: splitData.factor_value,
                          measure_entity_id: splitData.entity_id,
                          selected_measure_entity_id: selectedMeasureId,
                          isSelectedMeasure: isEqual(
                            splitData.entity_id,
                            selectedMeasureId,
                          ),
                          [isOpenTransfer ? 'item_cost' : 'initial_item_cost']:
                            rowData.item_cost_mapper[splitData.entity_id],
                          initial_count:
                            splitData.entity_id === rowData.measure_entity_id
                              ? rowData.initial_count
                              : null,
                          measures: rowData.measures,
                        };
                      },
                    );
                  },
                },
                multiGrid: (
                  params,
                ):
                  | Record<string, IInventoryItem[]>
                  | PdfFiledValue<IInventoryItem>[] => {
                  return isCategoryView(
                    params.tools.services?.stateService?.getTransferItemListViewSetting(),
                  )
                    ? groupByMajorMinorCategoryAndSort(params?.data)
                    : [params.data];
                },
                orderBy: ['is_chargeable desc', 'item_name asc'],
              },
              [formFieldPath.virtualTotals]: {
                title: {
                  text: I18n.Common.Totals,
                  style: {
                    fontSize: 11,
                    bold: true,
                    margin: [0, 10, 0, 5],
                  },
                },
                rowStyle: (params): Style => {
                  return {
                    bold: params.gridData.length - 1 === params.rowIndex,
                  };
                },
                layout: {
                  fillColor: (rowIndex): string => {
                    return rowIndex % 2 === 0
                      ? self.greyBgColor
                      : self.whiteBgColor;
                  },
                },
                columnDefaultStyle: {
                  width: '*',
                  alignment: 'right',
                },
                columns: [
                  {
                    path: 'site_department_code_to',
                    title: I18n.EntityNames.DepartmentCode,
                    width: '*',
                    alignment: null,
                  },
                  {
                    path: 'category',
                    title: '',
                    width: '*',
                    alignment: 'left',
                    required: true,
                  },
                  {
                    path: 'name',
                    title: I18n.Forms.GridForm.GLAccount,
                    width: '*',
                    alignment: null,
                    required: true,
                  },
                  {
                    path: 'initial_count',
                    title: I18n.Forms.TransferForm.QtyRequested,
                    valueFormatter: ({ tools, ...params }): string =>
                      isOpenTransfer
                        ? ''
                        : transferPDFQuantityFormatter({ tools, ...params }),
                  },
                  {
                    path: 'total_cost',
                    title: I18n.Forms.TransferForm.RequestCost,
                    hiddenColumn: (): boolean => isOpenTransfer,
                    valueFormatter: ({ tools, ...params }): string =>
                      !isNil(params.value)
                        ? tools.services.numberFormatService.getNumberFormatter(
                            true,
                          )(params)
                        : null,
                  },
                  {
                    path: 'quantity_reversed',
                    title: I18n.Forms.TransferForm.QtyReversed,
                    valueFormatter: ({ tools, ...params }): string =>
                      isOpenTransfer
                        ? ''
                        : transferPDFQuantityFormatter({ tools, ...params }),
                    required: true,
                  },
                  {
                    path: 'reversed_cost',
                    title: I18n.Forms.TransferForm.ReversedCost,
                    hiddenColumn: (params): boolean => {
                      return (
                        isOpenTransfer ||
                        !find(params.options.selectedColumns, (column) =>
                          isEqual(
                            column.title,
                            I18n.Forms.TransferForm.RequestCost,
                          ),
                        )
                      );
                    },
                    valueFormatter: ({ tools, ...params }): string =>
                      !isNil(params.value)
                        ? tools.services.numberFormatService.getNumberFormatter(
                            true,
                          )(params)
                        : null,
                    required: true,
                  },
                  {
                    path: 'count',
                    title: I18n.Forms.TransferForm.QtyReceived,
                    hiddenColumn: (): boolean => isOpenTransfer,
                    valueGetter: (): string => '',
                  },
                ],
              },
            };
          },
        },
        signatures: {
          type: PdfContentType.Signatures,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          setting: (pdfData: IPdfData): string[] => {
            return [
              I18n.Forms.GridForm.RequestedBy,
              I18n.Forms.GridForm.TransferredBy,
              I18n.Forms.GridForm.ReceivedBy,
            ];
          },
        },
      },
      options: {
        pageMargins: [20, 50, 20, 60],
        columnSelector: {
          columns: !showQoh
            ? filterQohFromColumn([
                CHARGEABLE_SELECT_COLUMN_DEFINE,
                ...(isShowAllColumns
                  ? TRANSFER_PDF_EMPTY_HEADERS
                  : TRANSFER_PDF_EMPTY_HEADERS_FOR_OPEN_STATUS),
              ] as IPdfTableHeader[])
            : [
                CHARGEABLE_SELECT_COLUMN_DEFINE,
                ...(isShowAllColumns
                  ? TRANSFER_PDF_EMPTY_HEADERS
                  : TRANSFER_PDF_EMPTY_HEADERS_FOR_OPEN_STATUS),
              ],
          columnsFromGridPath: {
            gridPath: 'mainGrids',
          },
        },
      },
    };
  };
};

export {
  TransferWrittenSheetPdfConfigBuilder,
  getTransferWrittenSheetConfigBuilder,
};
